import { useCallback, useState } from 'react';
import { createApiUrl } from '../../../config/api';
import { useApiAuth } from '../../../hooks/useApiAuth';
import type { ChatMessage } from '../../ChatBox/Messages';

interface ReportTabProps {
  sessionId: string;
  userId: string;
  tabId: string;
  reportData?: {
    pdf_file_name?: string;
    word_file_name?: string;
  };
  onResponse: (response: any) => void;
  onReportMessage?: (message: ChatMessage) => void;
}

export default function ReportTab({ sessionId, userId, tabId, reportData, onResponse, onReportMessage }: ReportTabProps) {
  const { makeAuthenticatedRequest } = useApiAuth();
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleFileSelected = (files: File[]) => {
    if (files.length > 0) {
      setAttachedFile(files[0]);
    }
  };

  const handleRemoveFile = () => {
    setAttachedFile(null);
  };

  const uploadFile = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append("files", file);
    formData.append("session_id", sessionId);
    formData.append("user_id", userId);
    formData.append("overwrite", "true");
    formData.append("skip_tab_creation", "true");
    formData.append("user_message", "");

    try {
      const response = await fetch(createApiUrl("/file_handling/upload_from_user"), {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const responseData = await response.json();
      if (Array.isArray(responseData) && responseData.length > 0 && responseData[0].data) {
        const fileData = responseData[0].data as any;
        return fileData.file_name || null;
      }
      return null;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleGenerateReport = useCallback(async () => {
    setIsGenerating(true);
    try {
      let templateFileName = null;
      
      if (attachedFile) {
        templateFileName = await uploadFile(attachedFile);
      }
      
      const response = await makeAuthenticatedRequest(
        createApiUrl(`/report/generate/session_id/${sessionId}`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            document_name: templateFileName || null,
            tab_reference_id: tabId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const data = await response.json();
      onResponse([data]);
      
      if (onReportMessage) {
        onReportMessage({
          role: 'assistant',
          content: 'Your report has been generated successfully!',
          id: `report-${Date.now()}`,
          createdAt: new Date()
        });
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [attachedFile, sessionId, tabId, userId, onResponse, onReportMessage, makeAuthenticatedRequest]);

  // Show PDF viewer if report has been generated
  if (reportData?.pdf_file_name) {
    const pdfUrl = createApiUrl(
      `/file_handling/users/${userId}/sessions/${sessionId}/files/processed/file_types/pdf/${encodeURIComponent(reportData.pdf_file_name)}`
    );
    const wordUrl = reportData.word_file_name ? createApiUrl(
      `/file_handling/users/${userId}/sessions/${sessionId}/files/processed/file_types/word/${encodeURIComponent(reportData.word_file_name)}`
    ) : '';

    return (
      <div className="h-full w-full p-4">
        {/* PDF viewer */}
        <div className="border rounded-lg overflow-hidden bg-gray-50" style={{ height: '100%' }}>
          <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Report PDF</h3>
            <div className="flex gap-2">

              {reportData.word_file_name && (
                <a
                  href={wordUrl}
                  download
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Save as Word
                </a>
              )}
            </div>
          </div>
          <div className="overflow-auto p-2" style={{ height: 'calc(100% - 48px)' }}>
            <object
              data={pdfUrl}
              type="application/pdf"
              className="w-full h-full min-h-[600px]"
              style={{ minHeight: 'calc(100vh - 200px)' }}
            >
              <p className="p-4 text-gray-600">
                Unable to display PDF. <a href={pdfUrl} className="text-blue-600 underline" download>Download PDF</a>
              </p>
            </object>
          </div>
        </div>
      </div>
    );
  }

  // Show upload and generate interface
  return (
    <div className="h-full flex items-center justify-center p-8 bg-gray-50">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Generate Report</h2>
          <p className="text-gray-500">
            Create a custom report for this case
          </p>
        </div>

        <div className="space-y-6">
          {/* Template Upload Section */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800 mb-1">Custom Template (Optional)</h3>
                <p className="text-sm text-gray-600">
                  Upload a Word or PDF template to customize the report format. If no template is provided, a default format will be used.
                </p>
              </div>
            </div>

            {!attachedFile ? (
              <label className={`block border-2 border-dashed border-gray-300 rounded-lg p-8 text-center transition-colors ${
                isGenerating ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-blue-400 hover:bg-blue-50'
              }`}>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Click to upload template</p>
                    <p className="text-xs text-gray-500 mt-1">Supports PDF and Word documents</p>
                  </div>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx"
                  disabled={isGenerating}
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files) handleFileSelected(Array.from(files));
                  }}
                />
              </label>
            ) : (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{attachedFile.name}</p>
                      <p className="text-xs text-gray-500">{(attachedFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    disabled={isGenerating}
                    className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                    title="Remove file"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerateReport}
            disabled={isGenerating}
            className={`w-full py-4 rounded-lg font-semibold text-white transition-all transform ${
              isGenerating
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg active:scale-[0.98]'
            }`}
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating Report...
              </span>
            ) : (
              'Generate Report'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
